// 근무현황 (Labor) — 작업자 마스터 + 출퇴근 + 교대
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface Worker {
  worker_id: string
  name: string
  role: 'operator' | 'technician' | 'inspector' | 'supervisor'
  department: string | null
  certs: string[]
  active: number
  on_duty: boolean
  current_clock_in: string | null
  open_attendance_id: string | null
}

interface Shift {
  shift_id: string
  name: string
  start_time: string
  end_time: string
  color: string
}

interface Attendance {
  attendance_id: string
  worker_id: string
  worker_name: string
  role: string
  shift_id: string | null
  shift_name: string | null
  shift_color: string | null
  clock_in: string
  clock_out: string | null
  status: 'on_duty' | 'off_duty' | 'break'
}

function hoursSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

const ROLE_BADGE: Record<Worker['role'], string> = {
  operator: 'info', technician: 'warn', inspector: 'success', supervisor: '',
}

export function LaborPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'labor', 'write')

  const [workers, setWorkers] = useState<Worker[] | null>(null)
  const [shifts, setShifts] = useState<Shift[] | null>(null)
  const [attendance, setAttendance] = useState<Attendance[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [pickShift, setPickShift] = useState<{ workerId: string } | null>(null)

  const load = async () => {
    try {
      const [w, s, a] = await Promise.all([
        api.get<Worker[]>('/labor/workers'),
        api.get<Shift[]>('/labor/shifts'),
        api.get<Attendance[]>('/labor/attendance'),
      ])
      setWorkers(w); setShifts(s); setAttendance(a)
    } catch (e: any) { toast.push('error', e?.message ?? '조회 실패') }
  }
  useEffect(() => { load() }, [])

  const clockIn = async (workerId: string, shiftId?: string) => {
    setBusy(workerId)
    try {
      await api.post('/labor/clock-in', { worker_id: workerId, shift_id: shiftId })
      toast.push('success', `${workerId} 출근 처리`)
      setPickShift(null)
      load()
    } catch (e: any) { toast.push('error', e?.message ?? '실패') }
    finally { setBusy(null) }
  }

  const clockOut = async (workerId: string) => {
    setBusy(workerId)
    try {
      await api.post('/labor/clock-out', { worker_id: workerId })
      toast.push('success', `${workerId} 퇴근 처리`)
      load()
    } catch (e: any) { toast.push('error', e?.message ?? '실패') }
    finally { setBusy(null) }
  }

  const onDutyCount = useMemo(() => workers?.filter((w) => w.on_duty).length ?? 0, [workers])
  const totalActive = useMemo(() => workers?.filter((w) => w.active).length ?? 0, [workers])

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="users" /> {t('labor.title')}
      </h1>
      <p className="muted reveal">{t('labor.intro')}</p>

      {/* KPI */}
      <section className="reveal" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge success"><FontAwesomeIcon icon="right-to-bracket" /> 현재 근무중</span>
          <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
            {workers ? <Counter value={onDutyCount} suffix=" 명" /> : <Skeleton h={56} />}
          </p>
          {workers && (
            <p className="muted" style={{ marginBottom: 0, marginTop: '0.4rem', fontSize: 'var(--fs-sm)' }}>
              전체 활성 {totalActive}명 중 {totalActive > 0 ? Math.round((onDutyCount / totalActive) * 100) : 0}%
            </p>
          )}
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge info"><FontAwesomeIcon icon="users" /> 작업자</span>
          <p className="kpi-num">
            {workers ? <Counter value={totalActive} suffix=" 명" /> : <Skeleton h={36} />}
          </p>
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge"><FontAwesomeIcon icon="stopwatch" /> 교대 정의</span>
          <p className="kpi-num">
            {shifts ? <Counter value={shifts.length} suffix=" 조" /> : <Skeleton h={36} />}
          </p>
        </Card>
      </section>

      {/* 작업자 목록 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="w-h">
        <h2 id="w-h" style={{ fontSize: 'var(--fs-lg)' }}>작업자</h2>
        {!workers ? (
          <SkeletonRows rows={5} />
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '900px' } as React.CSSProperties}>
            <table className="table" aria-label="작업자 목록">
              <thead>
                <tr>
                  <th>ID</th><th>이름</th><th>역할</th><th>부서</th><th>자격</th><th>현재 상태</th><th></th>
                </tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.worker_id}>
                    <td className="mono">{w.worker_id}</td>
                    <td>{w.name}</td>
                    <td><span className={`badge ${ROLE_BADGE[w.role]}`}>{w.role}</span></td>
                    <td>{w.department ?? '—'}</td>
                    <td style={{ maxWidth: 240 }}>
                      {w.certs.length === 0 ? <span className="muted">—</span> :
                        w.certs.map((c) => <span key={c} className="badge" style={{ marginRight: 4 }}>{c}</span>)}
                    </td>
                    <td>
                      {w.on_duty ? (
                        <span className="badge success">
                          <FontAwesomeIcon icon="right-to-bracket" /> 근무중 · {w.current_clock_in && hoursSince(w.current_clock_in)}
                        </span>
                      ) : (
                        <span className="badge muted">off duty</span>
                      )}
                    </td>
                    <td>
                      {w.on_duty ? (
                        <Button variant="ghost" magnetic={false} disabled={!canWrite || busy === w.worker_id}
                          onClick={() => clockOut(w.worker_id)} aria-label={`${w.worker_id} 퇴근`}>
                          <FontAwesomeIcon icon={busy === w.worker_id ? 'circle-notch' : 'right-from-bracket'} spin={busy === w.worker_id} />
                          <span>퇴근</span>
                        </Button>
                      ) : (
                        <Button variant="accent" magnetic={false} disabled={!canWrite || !w.active}
                          onClick={() => setPickShift({ workerId: w.worker_id })} aria-label={`${w.worker_id} 출근`}>
                          <FontAwesomeIcon icon="right-to-bracket" />
                          <span>출근</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 교대 마스터 + 출퇴근 이력 */}
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))' }}>
        <Card glass tilt={false} spotlight={false} className="reveal" as="section">
          <h2 style={{ fontSize: 'var(--fs-lg)' }}>교대 정의</h2>
          {!shifts ? <SkeletonRows rows={3} /> : (
            <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.5rem' }}>
              {shifts.map((s) => (
                <li key={s.shift_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-soft)' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 999, background: s.color }} />
                  <strong>{s.name}</strong>
                  <span className="mono muted" style={{ marginLeft: 'auto' }}>{s.start_time} – {s.end_time}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card glass tilt={false} spotlight={false} className="reveal" as="section">
          <h2 style={{ fontSize: 'var(--fs-lg)' }}>최근 출퇴근</h2>
          {!attendance ? <SkeletonRows rows={4} /> : attendance.length === 0 ? (
            <p className="muted">출퇴근 기록 없음</p>
          ) : (
            <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.4rem' }}>
              {attendance.slice(0, 12).map((a) => (
                <li key={a.attendance_id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '0.6rem', alignItems: 'center', padding: '0.4rem 0.6rem', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-soft)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: a.shift_color ?? 'var(--color-line-strong)' }} />
                  <div style={{ fontSize: 'var(--fs-sm)' }}>
                    <strong>{a.worker_name}</strong> <span className="muted">({a.worker_id})</span>
                    {a.shift_name && <span className="muted"> · {a.shift_name}</span>}
                  </div>
                  <span className="mono muted" style={{ fontSize: 'var(--fs-xs)' }}>
                    {new Date(a.clock_in).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                    {a.clock_out && ` → ${new Date(a.clock_out).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* 출근 시 교대 선택 모달 */}
      {pickShift && (
        <div role="dialog" aria-modal="true" aria-label="교대 선택"
          onClick={(e) => { if (e.target === e.currentTarget) setPickShift(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: '1rem' }}
        >
          <Card glass tilt={false} spotlight={false} style={{ width: 'min(420px, 100%)' }}>
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>교대 선택 — {pickShift.workerId}</h2>
            <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>출근할 교대조를 선택하세요 (생략 가능).</p>
            <div className="stack" style={{ marginBlock: '1rem' }}>
              {shifts?.map((s) => (
                <Button key={s.shift_id} variant="ghost" magnetic={false}
                  onClick={() => clockIn(pickShift.workerId, s.shift_id)}
                  disabled={busy === pickShift.workerId}
                  style={{ justifyContent: 'flex-start' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 999, background: s.color }} />
                  <strong>{s.name}</strong>
                  <span className="mono muted" style={{ marginLeft: 'auto' }}>{s.start_time}–{s.end_time}</span>
                </Button>
              ))}
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <Button variant="ghost" magnetic={false} onClick={() => setPickShift(null)}>취소</Button>
              <Button variant="accent" magnetic={false}
                onClick={() => clockIn(pickShift.workerId)} disabled={busy === pickShift.workerId}>
                <FontAwesomeIcon icon={busy === pickShift.workerId ? 'circle-notch' : 'right-to-bracket'} spin={busy === pickShift.workerId} />
                <span>교대 없이 출근</span>
              </Button>
            </div>
          </Card>
        </div>
      )}
    </main>
  )
}
